import { addWindow, dispatch, getState, nextZ, subscribe } from '../state/store';
import type { AppContext, WindowInstance } from '../types/window';
import { APPS } from './apps/registry';
import { clampTitle, workArea } from './geometry';
import { iconUrl } from './icons';
import { playSfx } from '../audio/AudioEngine';

const mounted = new Map<string, { win: HTMLElement; unmount: () => void; body: HTMLElement }>();

function applyBox(node: HTMLElement, w: WindowInstance): void {
  const area = workArea();
  let x = w.x;
  let y = w.y;
  let width = w.w;
  let height = w.h;
  if (w.state === 'maximized') {
    x = area.left;
    y = area.top;
    width = area.width;
    height = area.height;
  }
  node.style.setProperty('--wx', `${x}px`);
  node.style.setProperty('--wy', `${y}px`);
  node.style.setProperty('--ww', `${width}px`);
  node.style.setProperty('--wh', `${height}px`);
  node.style.left = `${x}px`;
  node.style.top = `${y}px`;
  node.style.width = `${width}px`;
  node.style.height = `${height}px`;
  node.style.zIndex = String(w.z);
  node.classList.toggle('is-min', w.state === 'minimized');
  node.classList.toggle('is-focused', getState().focusedId === w.id);
}

function makeChrome(win: WindowInstance, defTitle: string): { root: HTMLElement; body: HTMLElement } {
  const root = document.createElement('div');
  root.className = 'w95-window';
  root.dataset.windowId = win.id;
  root.setAttribute('role', 'dialog');
  const titleId = `w95-title-${win.id}`;
  root.setAttribute('aria-labelledby', titleId);

  const bar = document.createElement('div');
  bar.className = 'w95-titlebar';
  const icon = document.createElement('img');
  icon.className = 'w95-title-icon';
  icon.alt = '';
  icon.src = iconUrl(win.icon);
  const title = document.createElement('span');
  title.className = 'w95-title';
  title.id = titleId;
  title.textContent = defTitle;
  const controls = document.createElement('div');
  controls.className = 'w95-controls';
  const min = document.createElement('button');
  min.setAttribute('aria-label', 'Minimize');
  min.textContent = '_';
  const max = document.createElement('button');
  max.setAttribute('aria-label', 'Maximize');
  max.textContent = '□';
  const close = document.createElement('button');
  close.setAttribute('aria-label', 'Close');
  close.textContent = '×';
  controls.append(min, max, close);
  bar.append(icon, title, controls);

  const body = document.createElement('div');
  body.className = 'w95-body';
  const grip = document.createElement('div');
  grip.className = 'w95-grip';
  grip.setAttribute('aria-hidden', 'true');
  root.append(bar, body, grip);

  root.addEventListener('pointerdown', () => dispatch({ type: 'FOCUS_WINDOW', id: win.id }));

  bar.addEventListener('pointerdown', (ev) => {
    if ((ev.target as HTMLElement).closest('button')) return;
    if (window.innerWidth < 700) return;
    ev.preventDefault();
    const inst = getState().windows.find((w) => w.id === win.id);
    if (!inst || inst.state === 'maximized') return;
    const sx = ev.clientX;
    const sy = ev.clientY;
    const ox = inst.x;
    const oy = inst.y;
    const move = (e: PointerEvent) => {
      const next = clampTitle(ox + e.clientX - sx, oy + e.clientY - sy, inst.w);
      root.style.left = `${next.x}px`;
      root.style.top = `${next.y}px`;
    };
    const up = (e: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const next = clampTitle(ox + e.clientX - sx, oy + e.clientY - sy, inst.w);
      dispatch({ type: 'MOVE_WINDOW', id: win.id, x: next.x, y: next.y });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  });

  bar.addEventListener('dblclick', () => {
    const inst = getState().windows.find((w) => w.id === win.id);
    if (!inst) return;
    dispatch({ type: inst.state === 'maximized' ? 'RESTORE' : 'MAXIMIZE', id: win.id });
  });

  grip.addEventListener('pointerdown', (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const inst = getState().windows.find((w) => w.id === win.id);
    if (!inst || inst.state === 'maximized') return;
    const sx = ev.clientX;
    const sy = ev.clientY;
    const ow = inst.w;
    const oh = inst.h;
    const move = (e: PointerEvent) => {
      const nw = Math.max(inst.minW, ow + e.clientX - sx);
      const nh = Math.max(inst.minH, oh + e.clientY - sy);
      root.style.width = `${nw}px`;
      root.style.height = `${nh}px`;
    };
    const up = (e: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      dispatch({
        type: 'RESIZE_WINDOW',
        id: win.id,
        w: Math.max(inst.minW, ow + e.clientX - sx),
        h: Math.max(inst.minH, oh + e.clientY - sy),
      });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  });

  min.addEventListener('click', () => {
    playSfx('minimize');
    dispatch({ type: 'MINIMIZE', id: win.id });
  });
  max.addEventListener('click', () => {
    const inst = getState().windows.find((w) => w.id === win.id);
    if (!inst) return;
    dispatch({ type: inst.state === 'maximized' ? 'RESTORE' : 'MAXIMIZE', id: win.id });
  });
  close.addEventListener('click', () => dispatch({ type: 'CLOSE_WINDOW', id: win.id }));

  return { root, body };
}

function createWindow(appId: string, payload?: Record<string, unknown>): void {
  const def = APPS[appId];
  if (!def) return;
  const area = workArea();
  const narrow = window.innerWidth < 700;
  const w = narrow ? area.width : def.defaultSize.w;
  const h = narrow ? area.height : def.defaultSize.h;
  const offset = (getState().windows.length % 6) * 22;
  const inst: WindowInstance = {
    id: crypto.randomUUID(),
    appId,
    title: def.title,
    icon: def.icon,
    x: area.left + 48 + offset,
    y: area.top + 36 + offset,
    w,
    h,
    minW: def.minSize?.w ?? 220,
    minH: def.minSize?.h ?? 140,
    z: nextZ(),
    state: narrow ? 'maximized' : 'normal',
    resizable: def.resizable !== false,
    payload,
  };
  const chrome = makeChrome(inst, def.title);
  const ctx: AppContext = {
    payload,
    openApp,
    close: () => dispatch({ type: 'CLOSE_WINDOW', id: inst.id }),
    setTitle: (t) => {
      const node = chrome.root.querySelector('.w95-title');
      if (node) node.textContent = t;
    },
  };
  const unmount = def.mount(chrome.body, ctx);
  mounted.set(appId, { win: chrome.root, unmount, body: chrome.body });
  document.getElementById('wm-root')?.append(chrome.root);
  addWindow(inst);
  applyBox(chrome.root, inst);
  playSfx('open');
}

export function openApp(appId: string, payload?: Record<string, unknown>): void {
  dispatch({ type: 'AUDIO_UNLOCK' });
  const existing = getState().windows.find((w) => w.appId === appId);
  if (existing) {
    dispatch({ type: 'OPEN_WINDOW', appId, payload });
    return;
  }
  createWindow(appId, payload);
}

export function initWindowManager(root: HTMLElement): void {
  subscribe((state, event) => {
    if (event.type === 'OPEN_WINDOW') {
      const def = APPS[event.appId];
      const rec = mounted.get(event.appId);
      const inst = state.windows.find((w) => w.appId === event.appId);
      if (def?.update && rec && inst) {
        def.update(rec.body, {
          payload: inst.payload,
          openApp,
          close: () => dispatch({ type: 'CLOSE_WINDOW', id: inst.id }),
          setTitle: (t) => {
            const node = rec.win.querySelector('.w95-title');
            if (node) node.textContent = t;
          },
        });
      }
    }
    if (event.type === 'CLOSE_WINDOW') {
      const gone = [...mounted.entries()].find(([, rec]) => rec.win.dataset.windowId === event.id);
      if (gone) {
        gone[1].unmount();
        gone[1].win.remove();
        mounted.delete(gone[0]);
      }
    }
    for (const w of state.windows) {
      const rec = mounted.get(w.appId);
      if (rec) applyBox(rec.win, w);
    }
  });
  void root;
}
